import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ArrowLeft, Activity, Play, RefreshCw, Server, Cpu,
  CheckCircle, XCircle, Clock, Loader2, AlertCircle,
  ChevronDown, ChevronRight, Plus, X, Send, Settings, HelpCircle,
  Folder, Hash, User, FileText
} from 'lucide-react'
import './JobMonitorView.css'

const CODEX_URL = 'http://localhost:8600'

// Parse codex stdout into pipeline phases
function parsePhases(stdout) {
  if (!stdout) return []
  const phases = []
  const lines = stdout.split('\n')

  // Detect high-level phases from the output
  let currentPhase = null
  for (const line of lines) {
    if (line.includes('Discovery:')) {
      phases.push({ name: 'Discovery', status: 'completed', detail: '' })
      currentPhase = phases[phases.length - 1]
    } else if (line.includes('Neo4j: connected')) {
      phases.push({ name: 'Neo4j Connect', status: 'completed', detail: line.trim() })
    } else if (line.includes('Pass 1')) {
      phases.push({ name: 'Pass 1 — Graph Load', status: 'completed', detail: '', programs: [] })
      currentPhase = phases[phases.length - 1]
    } else if (line.match(/^\[(\d+)\/(\d+)\]/)) {
      const match = line.match(/^\[(\d+)\/(\d+)\]\s*(\S+)/)
      if (match && currentPhase?.programs) {
        currentPhase.programs.push({ index: match[1], total: match[2], file: match[3] })
      }
    } else if (line.includes('-- OK')) {
      if (currentPhase?.programs?.length > 0) {
        const last = currentPhase.programs[currentPhase.programs.length - 1]
        last.result = line.trim()
      }
    } else if (line.includes('Cross-program CALLS:')) {
      phases.push({ name: 'Cross-program CALLS', status: 'completed', detail: '' })
      currentPhase = phases[phases.length - 1]
    } else if (line.includes('Field Ownership')) {
      phases.push({ name: 'Field Ownership Resolution', status: 'completed', detail: '' })
      currentPhase = phases[phases.length - 1]
    } else if (line.includes('Non-COBOL Asset')) {
      phases.push({ name: 'Non-COBOL Assets', status: 'completed', detail: '' })
      currentPhase = phases[phases.length - 1]
    } else if (line.includes('Analyzer:')) {
      phases.push({ name: 'Analyzer', status: 'completed', detail: '' })
      currentPhase = phases[phases.length - 1]
    } else if (line.includes('Rules loaded')) {
      phases.push({ name: 'Rules Loaded', status: 'completed', detail: line.trim() })
    } else if (line.includes('Pass 2')) {
      phases.push({ name: 'Pass 2 — Emit', status: 'completed', detail: '', programs: [] })
      currentPhase = phases[phases.length - 1]
    } else if (line.includes('Graph Visualization')) {
      phases.push({ name: 'Graph Visualization', status: 'completed', detail: '' })
      currentPhase = phases[phases.length - 1]
    } else if (line.includes('Ingest Complete')) {
      phases.push({ name: 'Complete', status: 'completed', detail: '' })
    } else if (line.includes('ERROR')) {
      if (currentPhase) {
        currentPhase.detail += (currentPhase.detail ? '\n' : '') + line.trim()
        currentPhase.hasError = true
      }
    }

    // Capture detail lines for current phase
    if (currentPhase && line.startsWith('  ') && !line.match(/^\[/) && !line.includes('-- OK')) {
      const trimmed = line.trim()
      if (trimmed && !phases.some(p => p.detail === trimmed)) {
        currentPhase.detail += (currentPhase.detail ? '\n' : '') + trimmed
      }
    }
  }
  return phases
}

// Format duration from ISO timestamps
function formatDuration(start, end) {
  if (!start) return '—'
  const s = new Date(start)
  const e = end ? new Date(end) : new Date()
  const ms = e - s
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`
}

function formatTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleTimeString()
}

const STATUS_CONFIG = {
  running:   { icon: Loader2, color: 'var(--accent-blue)',   label: 'Running',   spin: true },
  completed: { icon: CheckCircle, color: 'var(--accent-green)', label: 'Completed', spin: false },
  failed:    { icon: XCircle, color: 'var(--accent-red)',    label: 'Failed',    spin: false },
  queued:    { icon: Clock, color: 'var(--accent-yellow)',   label: 'Queued',    spin: false },
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.queued
  const Icon = cfg.icon
  return (
    <span className={`status-badge status-${status}`} style={{ color: cfg.color }}>
      <Icon size={14} className={cfg.spin ? 'spin' : ''} />
      <span>{cfg.label}</span>
    </span>
  )
}


function JobMonitorView({ onBack, onOpenSettings, onOpenHelp }) {
  // Health/dashboard state
  const [health, setHealth] = useState(null)
  const [serviceUp, setServiceUp] = useState(false)

  // Jobs state
  const [jobs, setJobs] = useState([])
  const [selectedJob, setSelectedJob] = useState(null)
  const [jobDetail, setJobDetail] = useState(null)
  const [filter, setFilter] = useState('all')

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true)
  const refreshIntervalRef = useRef(null)

  // Submit modal
  const [showSubmit, setShowSubmit] = useState(false)
  const [submitForm, setSubmitForm] = useState({
    app_dir: '',
    app_id: '',
    account_id: 'demo',
    copybook_dirs: [''],
    output_dir: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Raw log toggle
  const [showRawLog, setShowRawLog] = useState(false)

  // --- API calls ---

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch(`${CODEX_URL}/health`)
      if (res.ok) {
        const data = await res.json()
        setHealth(data)
        setServiceUp(true)
      } else {
        setServiceUp(false)
      }
    } catch {
      setServiceUp(false)
      setHealth(null)
    }
  }, [])

  const fetchJobs = useCallback(async () => {
    try {
      const res = await fetch(`${CODEX_URL}/analyze`)
      if (res.ok) {
        const data = await res.json()
        // Sort by created_at descending
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        setJobs(data)
      }
    } catch {
      // silent
    }
  }, [])

  const fetchJobDetail = useCallback(async (jobId) => {
    try {
      const res = await fetch(`${CODEX_URL}/analyze/${jobId}`)
      if (res.ok) {
        const data = await res.json()
        setJobDetail(data)
      }
    } catch {
      // silent
    }
  }, [])

  const submitAnalysis = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const body = {
        app_dir: submitForm.app_dir,
        app_id: submitForm.app_id,
        account_id: submitForm.account_id || 'demo',
        copybook_dirs: submitForm.copybook_dirs.filter(d => d.trim()),
      }
      if (submitForm.output_dir.trim()) {
        body.output_dir = submitForm.output_dir.trim()
      }

      const res = await fetch(`${CODEX_URL}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Request failed' }))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }

      const job = await res.json()
      setShowSubmit(false)
      setSubmitForm({ app_dir: '', app_id: '', account_id: 'demo', copybook_dirs: [''], output_dir: '' })
      setSelectedJob(job.job_id)
      fetchJobs()
      fetchJobDetail(job.job_id)
    } catch (err) {
      setSubmitError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  // --- Effects ---

  // Initial fetch
  useEffect(() => {
    fetchHealth()
    fetchJobs()
  }, [fetchHealth, fetchJobs])

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        fetchHealth()
        fetchJobs()
        if (selectedJob) fetchJobDetail(selectedJob)
      }, 3000)
    }
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current)
    }
  }, [autoRefresh, selectedJob, fetchHealth, fetchJobs, fetchJobDetail])

  // Fetch detail when job selected
  useEffect(() => {
    if (selectedJob) fetchJobDetail(selectedJob)
  }, [selectedJob, fetchJobDetail])

  // --- Computed ---

  const filteredJobs = filter === 'all' ? jobs : jobs.filter(j => j.status === filter)
  const runningCount = jobs.filter(j => j.status === 'running').length
  const completedCount = jobs.filter(j => j.status === 'completed').length
  const failedCount = jobs.filter(j => j.status === 'failed').length
  const totalPrograms = jobs.reduce((sum, j) => sum + (j.programs_succeeded || 0), 0)

  const phases = jobDetail ? parsePhases(jobDetail.stdout) : []

  // --- Render ---

  return (
    <div className="job-monitor">
      {/* Header */}
      <div className="jm-header">
        <div className="jm-header-left">
          <button className="jm-back-btn" onClick={onBack}>
            <ArrowLeft size={18} />
          </button>
          <Activity size={20} className="jm-logo" />
          <h1>CodeX Job Monitor</h1>
          <span className={`jm-service-badge ${serviceUp ? 'up' : 'down'}`}>
            <span className="jm-service-dot" />
            {serviceUp ? 'Service Online' : 'Service Offline'}
          </span>
        </div>
        <div className="jm-header-right">
          <button
            className={`jm-refresh-toggle ${autoRefresh ? 'active' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? 'Auto-refresh ON (3s)' : 'Auto-refresh OFF'}
          >
            <RefreshCw size={16} className={autoRefresh ? 'spin-slow' : ''} />
            <span>Auto</span>
          </button>
          <button className="jm-action-btn primary" onClick={() => setShowSubmit(true)}>
            <Plus size={16} />
            <span>New Analysis</span>
          </button>
          <button className="jm-icon-btn" onClick={onOpenSettings} title="Settings">
            <Settings size={18} />
          </button>
          <button className="jm-icon-btn" onClick={onOpenHelp} title="Help">
            <HelpCircle size={18} />
          </button>
        </div>
      </div>

      {/* Dashboard Cards */}
      <div className="jm-dashboard">
        <div className="jm-card">
          <div className="jm-card-header">
            <Server size={16} />
            <span>Service</span>
          </div>
          <div className="jm-card-value">
            {serviceUp ? (
              <span className="jm-value-green">{health?.workers || 0} Workers</span>
            ) : (
              <span className="jm-value-red">Offline</span>
            )}
          </div>
          <div className="jm-card-detail">
            {health ? `Active: ${health.active_jobs}` : 'Not connected'}
          </div>
        </div>

        <div className="jm-card">
          <div className="jm-card-header">
            <Activity size={16} />
            <span>Jobs</span>
          </div>
          <div className="jm-card-value">{jobs.length}</div>
          <div className="jm-card-detail">
            <span className="jm-stat-blue">{runningCount} running</span>
            {' / '}
            <span className="jm-stat-green">{completedCount} done</span>
            {failedCount > 0 && <>{' / '}<span className="jm-stat-red">{failedCount} failed</span></>}
          </div>
        </div>

        <div className="jm-card">
          <div className="jm-card-header">
            <Cpu size={16} />
            <span>Programs</span>
          </div>
          <div className="jm-card-value">{totalPrograms}</div>
          <div className="jm-card-detail">Total processed</div>
        </div>

        <div className="jm-card">
          <div className="jm-card-header">
            <FileText size={16} />
            <span>Binary</span>
          </div>
          <div className="jm-card-value" style={{ fontSize: '13px' }}>
            {health?.binary_exists ? 'Found' : 'Missing'}
          </div>
          <div className="jm-card-detail" title={health?.binary}>
            {health?.binary ? health.binary.split('/').pop() : '—'}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="jm-content">
        {/* Jobs List */}
        <div className={`jm-jobs-panel ${selectedJob ? 'narrow' : ''}`}>
          <div className="jm-panel-header">
            <h2>Jobs</h2>
            <div className="jm-filters">
              {['all', 'running', 'completed', 'failed'].map(f => (
                <button
                  key={f}
                  className={`jm-filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  {f !== 'all' && (
                    <span className="jm-filter-count">
                      {f === 'running' ? runningCount : f === 'completed' ? completedCount : failedCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {filteredJobs.length === 0 ? (
            <div className="jm-empty">
              <Activity size={32} />
              <p>{serviceUp ? 'No jobs yet. Submit an analysis to get started.' : 'Codex Service is offline.'}</p>
            </div>
          ) : (
            <div className="jm-jobs-list">
              {filteredJobs.map(job => (
                <div
                  key={job.job_id}
                  className={`jm-job-row ${selectedJob === job.job_id ? 'selected' : ''}`}
                  onClick={() => setSelectedJob(selectedJob === job.job_id ? null : job.job_id)}
                >
                  <div className="jm-job-status">
                    <StatusBadge status={job.status} />
                  </div>
                  <div className="jm-job-info">
                    <div className="jm-job-appid">{job.app_id}</div>
                    <div className="jm-job-meta">
                      <span>{job.account_id}</span>
                      <span className="jm-dot">·</span>
                      <span>{formatTime(job.created_at)}</span>
                    </div>
                  </div>
                  <div className="jm-job-stats">
                    <div className="jm-job-programs">
                      {job.programs_succeeded}/{job.programs_total} programs
                    </div>
                    <div className="jm-job-duration">
                      {formatDuration(job.started_at, job.completed_at)}
                    </div>
                  </div>
                  <ChevronRight size={16} className="jm-job-chevron" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Job Detail Panel */}
        {selectedJob && jobDetail && (
          <div className="jm-detail-panel">
            <div className="jm-detail-header">
              <div>
                <h2>{jobDetail.app_id}</h2>
                <div className="jm-detail-meta">
                  <StatusBadge status={jobDetail.status} />
                  <span className="jm-dot">·</span>
                  <span>{jobDetail.account_id}</span>
                  <span className="jm-dot">·</span>
                  <span>{formatDuration(jobDetail.started_at, jobDetail.completed_at)}</span>
                </div>
              </div>
              <button className="jm-close-btn" onClick={() => setSelectedJob(null)}>
                <X size={18} />
              </button>
            </div>

            {/* Error banner */}
            {jobDetail.error && (
              <div className="jm-error-banner">
                <AlertCircle size={16} />
                <span>{jobDetail.error}</span>
              </div>
            )}

            {/* Pipeline Phases */}
            <div className="jm-phases">
              <h3>Pipeline Phases</h3>
              {phases.length === 0 ? (
                <div className="jm-phases-empty">
                  {jobDetail.status === 'running' ? (
                    <><Loader2 size={16} className="spin" /> Processing...</>
                  ) : (
                    'No phase data available'
                  )}
                </div>
              ) : (
                <div className="jm-phase-list">
                  {phases.map((phase, i) => (
                    <div key={i} className={`jm-phase ${phase.hasError ? 'has-error' : ''}`}>
                      <div className="jm-phase-indicator">
                        {phase.status === 'completed' && !phase.hasError ? (
                          <CheckCircle size={14} className="jm-phase-icon done" />
                        ) : phase.hasError ? (
                          <AlertCircle size={14} className="jm-phase-icon error" />
                        ) : (
                          <Loader2 size={14} className="jm-phase-icon spin" />
                        )}
                        {i < phases.length - 1 && <div className="jm-phase-line" />}
                      </div>
                      <div className="jm-phase-content">
                        <div className="jm-phase-name">{phase.name}</div>
                        {phase.programs?.length > 0 && (
                          <div className="jm-phase-programs">
                            {phase.programs.map((p, j) => (
                              <div key={j} className="jm-phase-program">
                                <span className="jm-prog-index">[{p.index}/{p.total}]</span>
                                <span className="jm-prog-file">{p.file}</span>
                                {p.result && <span className="jm-prog-result">{p.result}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {phase.detail && (
                          <div className="jm-phase-detail">{phase.detail}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary stats */}
            {jobDetail.status === 'completed' && (
              <div className="jm-summary">
                <h3>Summary</h3>
                <div className="jm-summary-grid">
                  <div className="jm-summary-item">
                    <span className="jm-summary-label">Programs</span>
                    <span className="jm-summary-value">{jobDetail.programs_succeeded}/{jobDetail.programs_total}</span>
                  </div>
                  <div className="jm-summary-item">
                    <span className="jm-summary-label">Duration</span>
                    <span className="jm-summary-value">{formatDuration(jobDetail.started_at, jobDetail.completed_at)}</span>
                  </div>
                  <div className="jm-summary-item">
                    <span className="jm-summary-label">Started</span>
                    <span className="jm-summary-value">{formatTime(jobDetail.started_at)}</span>
                  </div>
                  <div className="jm-summary-item">
                    <span className="jm-summary-label">Completed</span>
                    <span className="jm-summary-value">{formatTime(jobDetail.completed_at)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Raw Log Toggle */}
            <div className="jm-raw-log">
              <button className="jm-raw-toggle" onClick={() => setShowRawLog(!showRawLog)}>
                {showRawLog ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span>Raw Output</span>
              </button>
              {showRawLog && (
                <pre className="jm-raw-content">{jobDetail.stdout || '(no output)'}</pre>
              )}
              {showRawLog && jobDetail.stderr && (
                <>
                  <div className="jm-raw-stderr-label">stderr:</div>
                  <pre className="jm-raw-content stderr">{jobDetail.stderr}</pre>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Submit Modal */}
      {showSubmit && (
        <div className="jm-modal-overlay" onClick={() => setShowSubmit(false)}>
          <div className="jm-modal" onClick={e => e.stopPropagation()}>
            <div className="jm-modal-header">
              <h2>New Analysis</h2>
              <button className="jm-close-btn" onClick={() => setShowSubmit(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="jm-modal-body">
              {submitError && (
                <div className="jm-error-banner">
                  <AlertCircle size={16} />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="jm-form-group">
                <label><Folder size={14} /> App Directory</label>
                <input
                  type="text"
                  placeholder="/path/to/cobol/source"
                  value={submitForm.app_dir}
                  onChange={e => setSubmitForm({ ...submitForm, app_dir: e.target.value })}
                />
              </div>

              <div className="jm-form-group">
                <label><Hash size={14} /> App ID</label>
                <input
                  type="text"
                  placeholder="my-app-name"
                  value={submitForm.app_id}
                  onChange={e => setSubmitForm({ ...submitForm, app_id: e.target.value })}
                />
              </div>

              <div className="jm-form-group">
                <label><User size={14} /> Account ID</label>
                <input
                  type="text"
                  placeholder="demo"
                  value={submitForm.account_id}
                  onChange={e => setSubmitForm({ ...submitForm, account_id: e.target.value })}
                />
              </div>

              <div className="jm-form-group">
                <label><Folder size={14} /> Copybook Directories</label>
                {submitForm.copybook_dirs.map((dir, i) => (
                  <div key={i} className="jm-form-row">
                    <input
                      type="text"
                      placeholder="/path/to/copybooks"
                      value={dir}
                      onChange={e => {
                        const updated = [...submitForm.copybook_dirs]
                        updated[i] = e.target.value
                        setSubmitForm({ ...submitForm, copybook_dirs: updated })
                      }}
                    />
                    {submitForm.copybook_dirs.length > 1 && (
                      <button
                        className="jm-remove-btn"
                        onClick={() => {
                          const updated = submitForm.copybook_dirs.filter((_, j) => j !== i)
                          setSubmitForm({ ...submitForm, copybook_dirs: updated })
                        }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  className="jm-add-btn"
                  onClick={() => setSubmitForm({ ...submitForm, copybook_dirs: [...submitForm.copybook_dirs, ''] })}
                >
                  <Plus size={14} /> Add Directory
                </button>
              </div>

              <div className="jm-form-group">
                <label><Folder size={14} /> Output Directory <span className="optional">(optional)</span></label>
                <input
                  type="text"
                  placeholder="/tmp/codex-output"
                  value={submitForm.output_dir}
                  onChange={e => setSubmitForm({ ...submitForm, output_dir: e.target.value })}
                />
              </div>
            </div>
            <div className="jm-modal-footer">
              <button className="jm-cancel-btn" onClick={() => setShowSubmit(false)}>
                Cancel
              </button>
              <button
                className="jm-submit-btn"
                onClick={submitAnalysis}
                disabled={submitting || !submitForm.app_dir || !submitForm.app_id}
              >
                {submitting ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                <span>{submitting ? 'Submitting...' : 'Submit Analysis'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JobMonitorView
